import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Wallet, Receipt, Banknote, DollarSign, ArrowUpCircle, X, TrendingUp, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Toast } from '../Toast';
import { GlassButton } from '../components/ui/GlassButton';

const formatBRL = (value: number | string) =>
    `R$ ${(typeof value === 'string' ? parseFloat(value) : value || 0).toFixed(2)}`;

const formatCpf = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

const formatPixKey = (value: string, type: string) => {
    if (!value) return '';
    if (type === 'cpf') {
        const v = value.replace(/\D/g, '').substring(0, 11);
        return formatCpf(v);
    }
    if (type === 'cnpj') {
        let v = value.replace(/\D/g, '').substring(0, 14);
        if (v.length <= 2) return v;
        if (v.length <= 5) return `${v.slice(0, 2)}.${v.slice(2)}`;
        if (v.length <= 8) return `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5)}`;
        if (v.length <= 12) return `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5, 8)}/${v.slice(8)}`;
        return `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5, 8)}/${v.slice(8, 12)}-${v.slice(12, 14)}`;
    }
    if (type === 'phone') {
        let v = value.replace(/\D/g, '').substring(0, 11);
        if (v.length === 0) return '';
        if (v.length <= 2) return `(${v}`;
        if (v.length <= 6) return `(${v.slice(0, 2)}) ${v.slice(2)}`;
        if (v.length <= 10) return `(${v.slice(0, 2)}) ${v.slice(2, 6)}-${v.slice(6)}`;
        return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7, 11)}`;
    }
    if (type === 'email') {
        return value.toLowerCase();
    }
    return value; // random
};

const formatAmountForInput = (value: number) => value.toFixed(2).replace('.', ',');

const parseAmountInput = (input: string): number => {
    const normalized = input.replace(/,/g, '.').replace(/[^\d.]/g, '');
    const parts = normalized.split('.');
    if (parts.length > 2) return parseFloat(parts[0] + '.' + parts.slice(1).join('')) || 0;
    return parseFloat(normalized) || 0;
};

// ─── Inline Styles ───
const glassCard: React.CSSProperties = {
    background: 'var(--glass-bg)',
    backdropFilter: 'blur(12px)',
    border: 'var(--glass-border)',
    borderRadius: 'var(--radius)',
    padding: '20px',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
};

const tableContainer: React.CSSProperties = {
    background: 'var(--glass-bg)',
    backdropFilter: 'blur(12px)',
    border: 'var(--glass-border)',
    borderRadius: 'var(--radius)',
    overflow: 'hidden',
};

const thStyle: React.CSSProperties = {
    padding: '14px 16px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.02)',
};

const tdStyle: React.CSSProperties = {
    padding: '14px 16px',
    fontSize: '13px',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
};

const inputStyle: React.CSSProperties = {
    width: '100%',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    padding: '10px 14px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s ease',
};

const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '11px',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '6px',
    fontWeight: 600,
};

const StatusBadge: React.FC<{ status: string; type: 'tx' | 'wd' }> = ({ status, type }) => {
    const s = String(status).toLowerCase();
    let bg = 'rgba(255,255,255,0.06)';
    let color = '#888';
    let icon = <AlertCircle size={12} />;
    let label = status || '—';

    if (type === 'tx') {
        if (s === 'paid') { bg = 'rgba(34,197,94,0.12)'; color = '#4ade80'; icon = <CheckCircle size={12} />; label = 'Pago'; }
        else if (s === 'pending') { bg = 'rgba(251,146,60,0.12)'; color = '#fb923c'; icon = <Clock size={12} />; label = 'Pendente'; }
        else if (s === 'failed' || s === 'refunded') { bg = 'rgba(239,68,68,0.12)'; color = '#f87171'; icon = <XCircle size={12} />; label = s === 'refunded' ? 'Reembolso' : 'Falhou'; }
    } else {
        if (s === 'completed') { bg = 'rgba(34,197,94,0.12)'; color = '#4ade80'; icon = <CheckCircle size={12} />; label = 'Concluído'; }
        else if (s === 'processing') { bg = 'rgba(59,130,246,0.12)'; color = '#60a5fa'; icon = <Clock size={12} />; label = 'Processando'; }
        else if (s === 'pending') { bg = 'rgba(251,146,60,0.12)'; color = '#fb923c'; icon = <Clock size={12} />; label = 'Pendente'; }
        else if (s === 'rejected') { bg = 'rgba(239,68,68,0.12)'; color = '#f87171'; icon = <XCircle size={12} />; label = 'Rejeitado'; }
    }

    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            background: bg, color, padding: '4px 10px', borderRadius: '20px',
            fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em'
        }}>
            {icon} {label}
        </span>
    );
};

// ─── Shimmer ───
const ShimmerRow: React.FC<{ cols: number }> = ({ cols }) => (
    <tr>
        {Array.from({ length: cols }).map((_, i) => (
            <td key={i} style={tdStyle}>
                <div className="shimmer-loading" style={{ height: '14px', borderRadius: '6px', width: `${50 + Math.random() * 40}%` }}></div>
            </td>
        ))}
    </tr>
);

export const Financeiro: React.FC = () => {
    const { user } = useAuth();
    const userId = user?.id;

    const [balance, setBalance] = useState<number>(0);
    const [totalWithdrawn, setTotalWithdrawn] = useState<number>(0);
    const [transactions, setTransactions] = useState<any[]>([]);
    const [withdrawals, setWithdrawals] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [pixKey, setPixKey] = useState('');
    const [pixKeyType, setPixKeyType] = useState<string>('cpf');
    const [ownerName, setOwnerName] = useState('');
    const [ownerDocument, setOwnerDocument] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [withdrawalInfo, setWithdrawalInfo] = useState<{ saved: boolean; pix_key_type?: string; pix_key?: string; owner_name?: string; owner_document?: string } | null>(null);
    const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);

    const loadWithdrawalInfo = useCallback(async () => {
        if (!userId) return;
        try {
            const res = await api.get('/wallet/withdrawal-info');
            setWithdrawalInfo(res.data.saved ? {
                saved: true,
                pix_key_type: res.data.pix_key_type,
                pix_key: res.data.pix_key,
                owner_name: res.data.owner_name,
                owner_document: res.data.owner_document
            } : { saved: false });
        } catch {
            setWithdrawalInfo({ saved: false });
        }
    }, [userId]);

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/[^\d,]/g, '');
        const parts = raw.split(',');
        const sanitized = parts.length > 1
            ? parts[0] + ',' + parts.slice(1).join('').slice(0, 2)
            : raw;
        const num = parseAmountInput(sanitized);
        if (num > balance) {
            setWithdrawAmount(formatAmountForInput(balance));
            return;
        }
        setWithdrawAmount(sanitized);
    };

    const applyAmountCap = () => {
        const num = parseAmountInput(withdrawAmount);
        if (num > balance) setWithdrawAmount(formatAmountForInput(balance));
    };

    const openWithdrawModal = () => {
        setWithdrawAmount('');
        if (withdrawalInfo?.saved && withdrawalInfo.owner_document) {
            setPixKeyType(withdrawalInfo.pix_key_type ?? 'cpf');
            setPixKey(withdrawalInfo.pix_key ?? '');
            setOwnerName(withdrawalInfo.owner_name ?? '');
            setOwnerDocument(withdrawalInfo.owner_document.length === 11 ? formatCpf(withdrawalInfo.owner_document) : withdrawalInfo.owner_document);
        } else {
            setPixKeyType('cpf');
            setPixKey('');
            setOwnerName('');
            setOwnerDocument('');
        }
        setWithdrawModalOpen(true);
    };

    const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        const digits = v.replace(/\D/g, '').slice(0, 11);
        setOwnerDocument(digits.length <= 11 ? formatCpf(digits) : ownerDocument);
    };

    const handleWithdrawSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!userId) {
            setToast({ msg: 'Faça login para solicitar saque.', type: 'error' });
            return;
        }
        const value = Math.min(parseAmountInput(withdrawAmount), balance);
        if (!value || value <= 0) {
            setToast({ msg: 'Informe um valor válido.', type: 'error' });
            return;
        }
        if (value > balance) {
            setToast({ msg: `O valor não pode ser maior que o saldo disponível (${formatBRL(balance)}).`, type: 'error' });
            return;
        }
        if (!pixKey.trim()) {
            setToast({ msg: 'Informe a chave PIX.', type: 'error' });
            return;
        }
        const docDigits = ownerDocument.replace(/\D/g, '');
        if (docDigits.length !== 11 && docDigits.length !== 14) {
            setToast({ msg: 'CPF deve ter 11 dígitos ou CNPJ 14 dígitos.', type: 'error' });
            return;
        }
        if (!ownerName.trim()) {
            setToast({ msg: 'Informe o nome do titular (sem acentos).', type: 'error' });
            return;
        }
        try {
            setSubmitting(true);
            await api.post('/wallet/withdraw', {
                amount: value,
                pixKey: pixKey.trim(),
                pixKeyType: pixKeyType || 'cpf',
                owner_name: ownerName.trim(),
                owner_document: docDigits
            });
            setToast({ msg: 'Solicitação de saque enviada. Aguarde a aprovação.', type: 'success' });
            setWithdrawAmount('');
            setWithdrawModalOpen(false);
            await loadWithdrawalInfo();
            await loadAll();
        } catch (err: unknown) {
            const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Erro ao solicitar saque.';
            setToast({ msg, type: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const loadAll = async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const [balanceRes, txRes, wdRes] = await Promise.all([
                api.get('/wallet/balance', { params: { userId } }),
                api.get('/wallet/transactions'),
                api.get('/wallet/withdrawals')
            ]);
            setBalance(balanceRes.data.balance ?? 0);
            setTransactions(txRes.data.transactions ?? []);
            setWithdrawals(wdRes.data.withdrawals ?? []);
            setTotalWithdrawn(wdRes.data.total_withdrawn ?? 0);
        } catch (e) {
            console.error('Erro ao carregar financeiro:', e);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { loadAll(); }, [userId]);
    useEffect(() => { loadWithdrawalInfo(); }, [loadWithdrawalInfo]);

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            {/* ─── Header ─── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
                <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
                    <Wallet size={22} color="var(--primary)" /> Financeiro
                </h1>
                <GlassButton type="button" onClick={openWithdrawModal}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <Banknote size={16} /> Solicitar Saque
                    </span>
                </GlassButton>
            </div>

            {/* ─── KPI Cards ─── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '28px' }}>
                <div style={glassCard}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(34,197,94,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <DollarSign size={18} color="#4ade80" />
                        </div>
                        <span style={{ fontSize: '12px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Saldo Disponível</span>
                    </div>
                    {loading ? (
                        <div className="shimmer-loading" style={{ height: '28px', width: '60%', borderRadius: '6px' }}></div>
                    ) : (
                        <span style={{ fontSize: '24px', fontWeight: 700, color: '#4ade80' }}>{formatBRL(balance)}</span>
                    )}
                    <p style={{ fontSize: '11px', color: '#555', marginTop: '8px' }}>Valor disponível para saque</p>
                </div>

                <div style={glassCard}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255, 106, 0, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ArrowUpCircle size={18} color="var(--primary)" />
                        </div>
                        <span style={{ fontSize: '12px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Sacado</span>
                    </div>
                    {loading ? (
                        <div className="shimmer-loading" style={{ height: '28px', width: '60%', borderRadius: '6px' }}></div>
                    ) : (
                        <span style={{ fontSize: '24px', fontWeight: 700, color: '#fff' }}>{formatBRL(totalWithdrawn)}</span>
                    )}
                    <p style={{ fontSize: '11px', color: '#555', marginTop: '8px' }}>Soma dos saques concluídos</p>
                </div>

                <div style={glassCard}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(59,130,246,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <TrendingUp size={18} color="#60a5fa" />
                        </div>
                        <span style={{ fontSize: '12px', color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Transações</span>
                    </div>
                    {loading ? (
                        <div className="shimmer-loading" style={{ height: '28px', width: '40%', borderRadius: '6px' }}></div>
                    ) : (
                        <span style={{ fontSize: '24px', fontWeight: 700, color: '#fff' }}>{transactions.length}</span>
                    )}
                    <p style={{ fontSize: '11px', color: '#555', marginTop: '8px' }}>Total de transações registradas</p>
                </div>
            </div>

            {/* ─── Transactions Table ─── */}
            <div style={{ marginBottom: '28px' }}>
                <h2 style={{ fontSize: '16px', fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                    <Receipt size={18} color="var(--primary)" /> Transações
                </h2>
                <div style={tableContainer}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Data</th>
                                    <th style={thStyle}>Valor Bruto</th>
                                    <th style={thStyle}>Taxa</th>
                                    <th style={thStyle}>Líquido</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={thStyle}>Método</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <>
                                        <ShimmerRow cols={6} />
                                        <ShimmerRow cols={6} />
                                        <ShimmerRow cols={6} />
                                    </>
                                ) : transactions.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#555', padding: '40px 16px' }}>
                                            Nenhuma transação encontrada.
                                        </td>
                                    </tr>
                                ) : (
                                    transactions.map(tx => (
                                        <tr key={tx.id} style={{ transition: 'background 0.15s ease' }}
                                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>
                                            <td style={{ ...tdStyle, color: '#aaa', whiteSpace: 'nowrap', fontSize: '12px' }}>
                                                {new Date(tx.created_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                            </td>
                                            <td style={{ ...tdStyle, color: '#fff', fontWeight: 500 }}>{formatBRL(tx.gross_amount ?? 0)}</td>
                                            <td style={{ ...tdStyle, color: '#fb923c' }}>{formatBRL(tx.platform_fee ?? 0)}</td>
                                            <td style={{ ...tdStyle, color: '#4ade80', fontWeight: 600 }}>{formatBRL(tx.net_amount ?? 0)}</td>
                                            <td style={tdStyle}><StatusBadge status={tx.status} type="tx" /></td>
                                            <td style={{ ...tdStyle, color: '#888', fontSize: '11px', textTransform: 'uppercase' }}>{tx.payment_method || 'pix'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* ─── Withdrawals Table ─── */}
            <div>
                <h2 style={{ fontSize: '16px', fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                    <Banknote size={18} color="var(--primary)" /> Saques
                </h2>
                <div style={tableContainer}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Data</th>
                                    <th style={thStyle}>Valor Solicitado</th>
                                    <th style={thStyle}>Taxa</th>
                                    <th style={thStyle}>Líquido</th>
                                    <th style={thStyle}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <>
                                        <ShimmerRow cols={5} />
                                        <ShimmerRow cols={5} />
                                    </>
                                ) : withdrawals.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#555', padding: '40px 16px' }}>
                                            Nenhum saque encontrado.
                                        </td>
                                    </tr>
                                ) : (
                                    withdrawals.map(wd => (
                                        <tr key={wd.id} style={{ transition: 'background 0.15s ease' }}
                                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>
                                            <td style={{ ...tdStyle, color: '#aaa', whiteSpace: 'nowrap', fontSize: '12px' }}>
                                                {new Date(wd.created_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                                            </td>
                                            <td style={{ ...tdStyle, color: '#fff', fontWeight: 500 }}>{formatBRL(wd.requested_amount ?? 0)}</td>
                                            <td style={{ ...tdStyle, color: '#fb923c' }}>{formatBRL(wd.withdrawal_fee ?? 0)}</td>
                                            <td style={{ ...tdStyle, color: '#4ade80', fontWeight: 600 }}>{formatBRL(wd.net_amount ?? 0)}</td>
                                            <td style={tdStyle}><StatusBadge status={wd.status} type="wd" /></td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* ─── Withdraw Modal ─── */}
            {withdrawModalOpen && (
                <div
                    style={{
                        position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '16px', background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)',
                        animation: 'fadeIn 0.2s ease'
                    }}
                    onClick={() => !submitting && setWithdrawModalOpen(false)}
                >
                    <div
                        style={{
                            background: '#111', border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: '16px', width: '100%', maxWidth: '480px',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.6)', maxHeight: '90vh', overflowY: 'auto',
                            animation: 'chartFadeSlideIn 0.3s ease-out'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={{ padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h2 style={{ fontSize: '16px', fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                                <Banknote size={18} color="var(--primary)" /> Solicitar Saque
                            </h2>
                            <button onClick={() => !submitting && setWithdrawModalOpen(false)} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', padding: '4px' }}>
                                <X size={20} />
                            </button>
                        </div>

                        {/* Body */}
                        <div style={{ padding: '22px' }}>
                            {/* Balance display */}
                            <div style={{
                                background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)',
                                borderRadius: '12px', padding: '14px 18px', marginBottom: '18px'
                            }}>
                                <p style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Saldo disponível</p>
                                <p style={{ fontSize: '20px', fontWeight: 700, color: '#4ade80', margin: 0 }}>{formatBRL(balance)}</p>
                            </div>

                            <p style={{ color: '#888', fontSize: '13px', marginBottom: '18px', lineHeight: '1.5' }}>
                                {withdrawalInfo?.saved
                                    ? 'Seus dados PIX estão salvos. Informe o valor e confirme.'
                                    : 'Preencha os dados do titular uma vez. Eles ficarão salvos.'}
                            </p>

                            <form onSubmit={handleWithdrawSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                <div>
                                    <label style={labelStyle}>Valor (R$) — máximo {formatBRL(balance)}</label>
                                    <input
                                        type="text"
                                        placeholder={formatAmountForInput(balance)}
                                        value={withdrawAmount}
                                        onChange={handleAmountChange}
                                        onBlur={applyAmountCap}
                                        style={inputStyle}
                                        onFocus={(e) => e.currentTarget.style.borderColor = 'rgba(255,106,0,0.5)'}
                                    />
                                </div>
                                <div>
                                    <label style={labelStyle}>Tipo da chave PIX</label>
                                    <select
                                        value={pixKeyType}
                                        onChange={e => {
                                            setPixKeyType(e.target.value);
                                            setPixKey(formatPixKey(pixKey, e.target.value));
                                        }}
                                        style={{ ...inputStyle, cursor: 'pointer' }}
                                    >
                                        <option value="cpf" style={{ background: '#1a1a1a' }}>CPF</option>
                                        <option value="cnpj" style={{ background: '#1a1a1a' }}>CNPJ</option>
                                        <option value="email" style={{ background: '#1a1a1a' }}>E-mail</option>
                                        <option value="phone" style={{ background: '#1a1a1a' }}>Telefone</option>
                                        <option value="evp" style={{ background: '#1a1a1a' }}>Chave Aleatória</option>
                                        <option value="copypaste" style={{ background: '#1a1a1a' }}>Copia e Cola</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={labelStyle}>Chave PIX</label>
                                    <input
                                        type="text"
                                        placeholder={pixKeyType === 'email' ? 'email@exemplo.com' : 'Chave PIX'}
                                        value={pixKey}
                                        onChange={e => setPixKey(formatPixKey(e.target.value, pixKeyType))}
                                        style={inputStyle}
                                        onFocus={(e) => e.currentTarget.style.borderColor = 'rgba(255,106,0,0.5)'}
                                    />
                                </div>
                                <div>
                                    <label style={labelStyle}>Nome do titular (sem acentos)</label>
                                    <input
                                        type="text"
                                        placeholder="Nome completo"
                                        value={ownerName}
                                        onChange={e => setOwnerName(e.target.value)}
                                        style={inputStyle}
                                        onFocus={(e) => e.currentTarget.style.borderColor = 'rgba(255,106,0,0.5)'}
                                    />
                                </div>
                                <div>
                                    <label style={labelStyle}>CPF do titular</label>
                                    <input
                                        type="text"
                                        placeholder="000.000.000-00"
                                        value={ownerDocument}
                                        onChange={handleDocumentChange}
                                        style={inputStyle}
                                        onFocus={(e) => e.currentTarget.style.borderColor = 'rgba(255,106,0,0.5)'}
                                    />
                                </div>
                                <GlassButton
                                    type="submit"
                                    disabled={submitting || balance <= 0}
                                    style={{ width: '100%', marginTop: '4px' }}
                                >
                                    {balance <= 0 ? 'Saldo indisponível' : submitting ? 'Enviando...' : 'Solicitar Saque'}
                                </GlassButton>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes chartFadeSlideIn {
                    from { opacity: 0; transform: translateY(20px) scale(0.97); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}</style>
        </div>
    );
};
